import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["autoStart", "Component", "render", "completionLingerTime", "incrementFactor", "incrementInterval", "initialValue", "maximum", "value"];
import { useState, useEffect, useRef } from 'react';
import { View } from '../../../components/View';
import { jsx as _jsx } from "react/jsx-runtime";
const COMPLETED_VALUE = 100;
export const AbstractProgress = props => {
  const {
      autoStart = true,
      Component: ProgressComponent,
      render,
      completionLingerTime = 500,
      incrementFactor = 0.25,
      incrementInterval = 150,
      initialValue = 0,
      maximum = COMPLETED_VALUE,
      value: propValue
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  const [running, setRunning] = useState(false);
  const [value, setValue] = useState(propValue !== undefined ? Math.max(initialValue, propValue) : initialValue);
  const timerId = useRef(null);
  const prevAutoStartRef = useRef(autoStart);
  const prevMaximumRef = useRef(maximum);
  const valueRef = useRef(value);
  const runningRef = useRef(running);
  useEffect(() => {
    valueRef.current = value;
    runningRef.current = running;
  }, [value, running]);
  function cancelNextUpdate() {
    if (timerId.current !== null) {
      clearTimeout(timerId.current);
      timerId.current = null;
    }
  }
  const schedule = (fn, increment = incrementInterval) => {
    cancelNextUpdate();
    timerId.current = window.setTimeout(fn, increment);
  };
  function scheduleNextUpdate() {
    if (valueRef.current <= maximum) {
      schedule(() => {
        incrementProgress();
      });
    }
  }
  function start() {
    setRunning(true);
    scheduleNextUpdate();
  }
  function isComplete(currentValue, nextValue) {
    return nextValue !== undefined && nextValue >= COMPLETED_VALUE || currentValue > COMPLETED_VALUE - 0.5 && nextValue === undefined;
  }
  function completeProgress() {
    schedule(() => {
      setValue(COMPLETED_VALUE);
      schedule(() => setRunning(false), completionLingerTime);
    });
  }
  function incrementProgress(nextValue) {
    const currentValue = valueRef.current;
    if (isComplete(currentValue, nextValue)) {
      completeProgress();
      return;
    }
    if (nextValue !== undefined) {
      setValue(Math.max(nextValue, initialValue));
      scheduleNextUpdate();
    } else {
      const newValue = currentValue + (maximum - currentValue) * incrementFactor;
      setValue(newValue);
      scheduleNextUpdate();
    }
  }
  useEffect(() => {
    if (autoStart) {
      start();
    }
    return () => {
      cancelNextUpdate();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (propValue !== undefined) {
      incrementProgress(propValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propValue]);
  useEffect(() => {
    const prevAutoStart = prevAutoStartRef.current;
    if (!prevAutoStart && autoStart) {
      start();
    }
    prevAutoStartRef.current = autoStart;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart]);

  // Add effect to handle maximum changes
  useEffect(() => {
    const prevMaximum = prevMaximumRef.current;
    if (prevMaximum !== maximum && running) {
      incrementProgress(value);
    }
    prevMaximumRef.current = maximum;
  }, [maximum]); // eslint-disable-line react-hooks/exhaustive-deps

  const done = !running && value >= COMPLETED_VALUE;
  const renderContent = () => {
    const progressProps = {
      done,
      value,
      easing: 'linear'
    };
    if (render) {
      return render(progressProps);
    }
    return ProgressComponent ? /*#__PURE__*/_jsx(ProgressComponent, Object.assign({}, progressProps)) : null;
  };
  return /*#__PURE__*/_jsx(View, Object.assign({}, rest, {
    children: renderContent()
  }));
};
AbstractProgress.displayName = 'AbstractProgress';